'use strict';

/**
 * @typedef {import('../freshpepper-frontend').UsercentricsService} UsercentricsService
 */
class CommonMethods {
    constructor () {
        /**
         * @type {Promise | undefined}
         * @private
         */
        this.cmpIsAvailablePromise = undefined

        /**
         * @type {Promise | undefined}
         * @private
         */
        this.cmpExplicitConsentPromise = undefined
    }

    /**
     * Returns a promise that resolves once the DOM is ready
     * this is useful for async loaded scripts
     *
     * @returns {Promise}
     */
    async isDomReady () {
        return new Promise((resolve) => {
            if (document.readyState === 'interactive' || document.readyState === 'complete') {
                resolve();
                return;
            }

            document.addEventListener('DOMContentLoaded', () => {
                resolve();
            });
        });
    }

    /**
     * Wait for Usercentrics to be available.
     */
    async waitForCmpIsAvailable ({ timeoutInSeconds } = {}) {
        if (this.cmpIsAvailablePromise != null) {
            return this.cmpIsAvailablePromise
        }

        const promise = new Promise((resolve, reject) => {
            if (window.UC_UI && window.UC_UI.isInitialized()) {
                resolve();
            } else {
                window.addEventListener('UC_UI_INITIALIZED', () => {
                    resolve();
                });
            }
        });

        this.cmpIsAvailablePromise = timeoutInSeconds > 0
            ? Promise.race([promise, this.timeoutAfterSeconds(timeoutInSeconds)])
            : promise;

        return this.cmpIsAvailablePromise
    }

    /**
     * Wait for an explicit Usercentrics consent decision from the user.
     */
    async waitForExplicitConsent ({ timeoutInSeconds } = {}) {
        const promise = new Promise((resolve, reject) => {
            this.waitForCmpIsAvailable().then(() => {
                resolve();
            }).catch(() => {});
        });

        this.cmpExplicitConsentPromise = timeoutInSeconds > 0
            ? Promise.race([promise, this.timeoutAfterSeconds(timeoutInSeconds)])
            : promise;

        return this.cmpExplicitConsentPromise
    }

    /**
     * Returns a promise that rejects after the given `timeoutInSeconds`.
     *
     * @param {Number} timeoutInSeconds  the number of seconds before timing out
     *
     * @returns {Promise<void>}
     */
    timeoutAfterSeconds (timeoutInSeconds) {
        return new Promise((resolve, reject) => {
            const id = setTimeout(() => {
                clearTimeout(id);
                reject(new Error(`timeout after ${timeoutInSeconds} seconds`));
            }, timeoutInSeconds * 1000);
        });
    }

    /**
     * Get consent status for IAB Vendor
     *
     * @param {string[]} vendorTemplateIds Array of Usercentrics Vendor TemplateIds
     *
     * @returns {Promise<UsercentricsService[]>} array of UC service objects incl. consent status
     */
    async getConsents (vendorTemplateIds = []) {
        if (!Array.isArray(vendorTemplateIds)) {
            throw new TypeError('vendorTemplateIds must be an array or undefined');
        }

        await this.waitForCmpIsAvailable();
        let consents = window.UC_UI.getServicesBaseInfo();

        if (vendorTemplateIds.length > 0) {
            consents = consents.filter(service => vendorTemplateIds.includes(service.id));
        }

        return consents;
    }

    /**
     * Accept the consent for the given `vendorTemplateId`.
     *
     * @param {string} vendorTemplateId The Usercentrics Vendor Template ID
     * @param {'implicit' | 'explicit'} consentType
     *
     * @returns {Promise<void>}
     */
    async acceptConsent (vendorTemplateId, consentType = 'explicit') {
        if (!vendorTemplateId) {
            throw new TypeError('Cannot accept consent: "vendorTemplateId" argument must not be empty or nullish');
        }

        await this.waitForCmpIsAvailable();
        await window.UC_UI.acceptService(vendorTemplateId, consentType)
    }
}

export default CommonMethods;
